import { supabase } from '@/integrations/supabase/client';
import { ChatGroup } from '@/types/chat';

export const teamChatService = {
  // Get team chats for a user (including parent access)
  getTeamChats: async (userId: string): Promise<ChatGroup[]> => {
    try {
      const { data, error } = await supabase
        .from('chat_groups')
        .select(`
          chat_id,
          chat_name,
          created_by,
          created_at,
          updated_at,
          is_active,
          team_id,
          requires_team_code
        `)
        .eq('requires_team_code', true)
        .eq('is_active', true);

      if (error) throw error;

      // Filter to only include chats the user can access
      const accessibleChats: ChatGroup[] = [];

      for (const chat of data || []) {
        // Check if user is a member
        const { data: membership } = await supabase
          .from('chat_group_users')
          .select('user_id')
          .eq('chat_group_id', chat.chat_id)
          .eq('user_id', userId)
          .eq('is_active', true)
          .single();

        if (membership) {
          accessibleChats.push({
            chat_id: chat.chat_id,
            chat_name: chat.chat_name,
            created_by: chat.created_by,
            created_at: chat.created_at,
            updated_at: chat.updated_at,
            is_active: chat.is_active,
            team_id: chat.team_id
          });
          continue;
        }

        // Check if user can access as parent
        const { data: parentAccess } = await supabase
          .rpc('can_access_team_chat_as_parent', {
            p_user_id: userId,
            p_chat_group_id: chat.chat_id
          });

        if (parentAccess) {
          accessibleChats.push({
            chat_id: chat.chat_id,
            chat_name: chat.chat_name,
            created_by: chat.created_by,
            created_at: chat.created_at,
            updated_at: chat.updated_at,
            is_active: chat.is_active,
            team_id: chat.team_id
          });
        }
      }

      return accessibleChats;
    } catch (error) {
      console.error('Error fetching team chats:', error);
      throw error;
    }
  },

  // Create team chat for existing team
  createTeamChat: async (teamId: number, createdBy: string): Promise<ChatGroup | null> => {
    try {
      // Get team details and access code
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('team_name')
        .eq('team_id', teamId)
        .single();

      if (teamError) throw teamError;

      // Get access code separately
      const { data: accessCodeData, error: accessCodeError } = await supabase
        .from('team_access_codes')
        .select('access_code')
        .eq('team_id', teamId)
        .eq('is_active', true)
        .single();

      if (accessCodeError) throw accessCodeError;

      const accessCode = accessCodeData?.access_code;
      if (!accessCode) {
        throw new Error('Team must have an access code to create team chat');
      }

      const chatName = `${teamData.team_name} Code: ${accessCode}`;

      // Create the chat group
      const { data: chatData, error: chatError } = await supabase
        .from('chat_groups')
        .insert({
          chat_name: chatName,
          created_by: createdBy,
          team_id: teamId,
          requires_team_code: true
        })
        .select()
        .single();

      if (chatError) throw chatError;

      // Add creator to chat
      await supabase
        .from('chat_group_users')
        .insert({
          chat_group_id: chatData.chat_id,
          user_id: createdBy,
          is_active: true
        });

      // Add all team members to chat
      const { data: teamMembers } = await supabase
        .from('team_players')
        .select('user_id')
        .eq('team_id', teamId);

      if (teamMembers) {
        const memberInserts = teamMembers
          .filter(member => member.user_id !== createdBy)
          .map(member => ({
            chat_group_id: chatData.chat_id,
            user_id: member.user_id,
            is_active: true
          }));

        if (memberInserts.length > 0) {
          await supabase
            .from('chat_group_users')
            .insert(memberInserts);
        }
      }

      // Add team coaches to chat
      const { data: teamCoaches } = await supabase
        .from('team_coaches')
        .select('coach_id')
        .eq('team_id', teamId);

      if (teamCoaches) {
        const coachInserts = teamCoaches
          .filter(coach => coach.coach_id !== createdBy)
          .map(coach => ({
            chat_group_id: chatData.chat_id,
            user_id: coach.coach_id,
            is_active: true
          }));

        if (coachInserts.length > 0) {
          await supabase
            .from('chat_group_users')
            .insert(coachInserts);
        }
      }

      return {
        chat_id: chatData.chat_id,
        chat_name: chatData.chat_name,
        created_by: chatData.created_by,
        created_at: chatData.created_at,
        updated_at: chatData.updated_at,
        is_active: chatData.is_active,
        team_id: chatData.team_id
      };
    } catch (error) {
      console.error('Error creating team chat:', error);
      throw error;
    }
  },

  // Check if user can access team chat (including parent access)
  canAccessTeamChat: async (userId: string, chatGroupId: string): Promise<boolean> => {
    try {
      // Check direct membership
      const { data: membership } = await supabase
        .from('chat_group_users')
        .select('user_id')
        .eq('chat_group_id', chatGroupId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();

      if (membership) return true;

      // Check parent access
      const { data: parentAccess } = await supabase
        .rpc('can_access_team_chat_as_parent', {
          p_user_id: userId,
          p_chat_group_id: chatGroupId
        });

      return parentAccess || false;
    } catch (error) {
      console.error('Error checking team chat access:', error);
      return false;
    }
  },

  // Join team chat as parent
  joinTeamChatAsParent: async (userId: string, chatGroupId: string): Promise<boolean> => {
    try {
      // First verify parent can access
      const canAccess = await teamChatService.canAccessTeamChat(userId, chatGroupId);
      if (!canAccess) return false;

      // Add parent to chat group users
      const { error } = await supabase
        .from('chat_group_users')
        .insert({
          chat_group_id: chatGroupId,
          user_id: userId,
          is_active: true
        })
        .select()
        .single();

      return !error;
    } catch (error) {
      console.error('Error joining team chat as parent:', error);
      return false;
    }
  }
};